import { forwardRef, useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { tookFor } from '@/lib/ivyChat/chatNotices';
import { IvyMark, type IvyMarkProps } from '../IvyMark';
import './plan.css';

/**
 * The front door's message language, shared by /start's plan view and Studio B's chat so the two stay one design:
 * your message, Ivy's turn, the working line, her steps folded into one line, and the question card.
 */

export type MarkProps = Pick<IvyMarkProps, 'state' | 'pulse'>;

/** Ivy's mark beside her turn. It stays still unless the turn passes her state: only the newest turn should. */
export const Mark = ({ mark }: { mark?: MarkProps }) => <IvyMark {...mark} still={!mark} />;

export const YourMessage = forwardRef<HTMLDivElement, { children: ReactNode }>(({ children }, ref) => (
  <div ref={ref} data-user-text className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap break-words rounded-2xl bg-b-raised px-4 py-2.5 text-b-read text-b-t1">
    {children}
  </div>
));
YourMessage.displayName = 'YourMessage';

/**
 * Ivy's mark, then her turn indented beside it. `aside` sits under the mark, in the column the text leaves empty.
 * `mark` is her state on the newest turn; without it the mark is still.
 */
export function IvyTurn({ children, aside, mark, className }: { children: ReactNode; aside?: ReactNode; mark?: MarkProps; className?: string }) {
  return (
    <div className={cn('flex gap-3', className)}>
      <div className="flex shrink-0 flex-col items-center gap-1">
        <Mark mark={mark} />
        {aside}
      </div>
      <div className="min-w-0 flex-1 space-y-4 pt-1">{children}</div>
    </div>
  );
}

/** What Ivy is doing now, in words, with the front door's shimmer. */
export const WorkingLine = ({ text }: { text: string }) => (
  <p key={text} role="status" className="plan-rise plan-shimmer text-b-read">
    {text}
  </p>
);

export interface ThreadStep {
  id: string;
  /** What it is doing: "Rendering scene". A thought's first sentence. */
  name: string;
  /** Once done: "Rendered scene". */
  done: string;
  /** What it worked on, when known: "Scene 2". */
  detail: string | null;
  state: 'running' | 'done' | 'error';
  kind?: 'tool' | 'thought';
  startedAt?: number;
  endedAt?: number;
  /** What happened, for a step that failed. */
  error?: string | null;
  /** A builder (a sub-agent) and the steps it ran. */
  builder?: boolean;
  children?: ThreadStep[];
}

const FOLDED_NAMES = 2;
const LIVE_ROWS = 3;

const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
/** The time, ticking once a second while live rows show. */
function useClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

const took = (s: ThreadStep) => (s.startedAt && s.endedAt ? clock(s.endedAt - s.startedAt) : null);

/**
 * A step that never reports back (a workflow load) counts as done once a newer step has finished; calls made side by
 * side keep running until their own results come, and builders run until they report.
 */
function settle(steps: ThreadStep[]): ThreadStep[] {
  return steps.map((s, i) => {
    if (s.state !== 'running' || s.builder || !steps.slice(i + 1).some((n) => n.state !== 'running')) return s;
    return { ...s, state: 'done', endedAt: s.endedAt ?? steps[i + 1]?.startedAt };
  });
}

const mark = (s: ThreadStep) => (s.state === 'error' ? '✗' : s.state === 'running' ? '…' : '✓');

function StepRow({ step, now, current, toggle, children }: { step: ThreadStep; now?: number; current?: boolean; toggle?: ReactNode; children?: ReactNode }) {
  const running = step.state === 'running';
  const time = running ? (step.startedAt && now ? clock(now - step.startedAt) : null) : took(step);
  const detail = (step.state === 'error' && step.error) || step.detail;
  return (
    <li className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
      {running ? (
        <span data-step-name role={current ? 'status' : undefined} className="plan-shimmer font-medium text-b-t1">{step.name}…</span>
      ) : (
        <span data-step-name className="text-b-t2">{mark(step)} {step.done}</span>
      )}
      {detail && (
        <>
          {' · '}
          <span data-step-detail className="min-w-0 flex-1 truncate">{detail}</span>
        </>
      )}
      {toggle}
      {time && <span data-step-time className="ml-auto flex-none pl-2 tabular-nums">{time}</span>}
      {children}
    </li>
  );
}

/** While Ivy works: the last finished steps with their times, the current step counting up, a builder's newest step under it. */
function LiveSteps({ steps }: { steps: ThreadStep[] }) {
  const [all, setAll] = useState(false);
  const now = useClock();
  const tools = settle(steps.filter((s) => s.kind !== 'thought'));
  const finished = tools.filter((s) => s.state !== 'running');
  const hidden = all ? 0 : Math.max(0, finished.length - LIVE_ROWS);
  const shown = new Set(finished.slice(hidden));
  const newest = tools.at(-1);
  const current = newest?.state === 'running' ? newest : null;
  return (
    <div className="space-y-1 text-b-meta text-b-t3">
      {hidden > 0 && (
        <button type="button" onClick={() => setAll(true)} className="text-b-t2 underline-offset-2 hover:text-b-t1 hover:underline">
          {hidden} earlier step{hidden === 1 ? '' : 's'}
        </button>
      )}
      <ol data-live-steps className="space-y-1">
        {tools.filter((s) => s.state === 'running' || shown.has(s)).map((s, i) => {
          const inner = s.state === 'running' && s.children?.length ? s.children.at(-1)! : null;
          return (
            <StepRow key={`${s.id}-${i}`} step={s} now={now} current={s === current}>
              {inner && (
                <div data-builder-line className="flex w-full min-w-0 items-baseline gap-2 pl-4">
                  <span>↳ {inner.state === 'running' ? inner.name : inner.done}</span>
                  {inner.detail && <span className="min-w-0 flex-1 truncate">· {inner.detail}</span>}
                  {inner.state === 'running' && inner.startedAt && <span data-step-time className="ml-auto flex-none tabular-nums">{clock(now - inner.startedAt)}</span>}
                </div>
              )}
            </StepRow>
          );
        })}
      </ol>
      {!current && <WorkingLine text="Thinking…" />}
    </div>
  );
}

/** Every step once the turn is done: her thinking, each step with its time, failures with what happened, builders opening their own steps. */
function OpenedSteps({ steps }: { steps: ThreadStep[] }) {
  const [openBuilders, setOpenBuilders] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setOpenBuilders((o) => { const n = new Set(o); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const settled = settle(steps.filter((s) => s.kind !== 'thought'));
  const byId = new Map(settled.map((s) => [s.id, s]));
  return (
    <ol aria-label="Steps" className="space-y-1">
      {steps.map((raw, i) => {
        if (raw.kind === 'thought') {
          return (
            <li key={`${raw.id}-${i}`} className="italic">
              <span data-step-name>Thinking: {raw.name}</span>
            </li>
          );
        }
        const s = byId.get(raw.id) ?? raw;
        const kids = s.children ?? [];
        return (
          <StepRow
            key={`${s.id}-${i}`}
            step={s}
            toggle={kids.length > 0 && (
              <button type="button" onClick={() => toggle(s.id)} aria-expanded={openBuilders.has(s.id)} className="flex-none text-b-t2 underline-offset-2 hover:text-b-t1 hover:underline">
                {kids.length} step{kids.length === 1 ? '' : 's'}
              </button>
            )}
          >
            {openBuilders.has(s.id) && (
              <ol className="w-full space-y-1 pl-4">
                {settle(kids).map((k, j) => <StepRow key={`${k.id}-${j}`} step={k} />)}
              </ol>
            )}
          </StepRow>
        );
      })}
    </ol>
  );
}

/**
 * Ivy's tool runs. While she works: live rows (F2). Once done: "✓ Wrote the plan · Show steps", which opens every step
 * and the turn's time.
 */
export function StepsLine({ steps, running, time }: { steps: ThreadStep[]; running: boolean; time: string | null }) {
  const [open, setOpen] = useState(false);
  const tools = steps.filter((s) => s.kind !== 'thought');
  if (running) return tools.length ? <LiveSteps steps={steps} /> : <WorkingLine text="Thinking…" />;
  if (!tools.length) return null;

  const names = [...new Set(tools.filter((s) => s.state === 'done').map((s) => s.done))];
  const failed = tools.filter((s) => s.state === 'error').length;
  const shown = names.slice(-FOLDED_NAMES);
  const parts = [...shown.map((n) => `✓ ${n}`), ...(failed ? [`${failed} step${failed === 1 ? '' : 's'} didn’t work`] : [])];
  const tookTurn = tookFor(time);
  return (
    <div className="text-b-meta text-b-t3">
      <p>
        {parts.map((part) => <span key={part}>{part} · </span>)}
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="text-b-t2 underline-offset-2 hover:text-b-t1 hover:underline">
          {open ? 'Hide steps' : tools.length > FOLDED_NAMES ? `Show all ${tools.length} steps` : 'Show steps'}
        </button>
      </p>
      {open && (
        <div className="plan-rise mt-2 space-y-1">
          <OpenedSteps steps={steps} />
          {tookTurn && <p>{tookTurn}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * Every stop (plan ready, look, voice, review) in one card: one border, the recommended answer as the ink button.
 * Its spacing is its own (gaps, no margins): it is drawn inside Ivy's markdown, whose heading and paragraph margins
 * would otherwise reach in.
 */
export function QuestionCard({ title, facts, hint, children }: { title?: string; facts?: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div data-question-card className="my-1 flex max-w-md flex-col gap-3 rounded-[var(--b-radius-card)] border border-b-line bg-b-elevated p-4">
      {title && (
        <div className="flex flex-col gap-0.5">
          <h3 className="!m-0 !text-b-label text-b-t1">{title}</h3>
          {facts && <p className="!m-0 text-b-meta text-b-t3">{facts}</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {hint && <p className="!m-0 text-b-meta text-b-t3">{hint}</p>}
    </div>
  );
}

export function QuestionButton({ label, recommended = false, disabled = false, onClick }: { label: string; recommended?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-description={recommended ? 'Recommended' : undefined}
      className={cn(
        'inline-flex h-9 items-center rounded-[var(--b-radius-control)] px-3.5 text-b-control transition-colors disabled:cursor-default disabled:opacity-50',
        recommended ? 'bg-b-ink text-b-on-ink hover:opacity-90' : 'bg-b-raised text-b-t1 hover:bg-b-elevated',
      )}
    >
      {label}
    </button>
  );
}
